/**
 * Google Analytics 4 (GA4) with Consent Mode v2.
 *
 * The gtag script and `consent default: denied` are loaded statically in
 * index.html so Google detects the tag on first byte (which is what makes
 * Realtime's "Active Users" widget populate — it's wired to the config
 * call, not to events). Until consent is granted, GA buffers hits locally
 * and sends nothing to Google's servers.
 *
 * This module just flips the consent switch and routes page_view events on
 * client-side route changes (gtag's auto-page-view doesn't trigger in an
 * SPA).
 *
 * App routes (/dashboard, /guest, /admin, /sign-in) are excluded from
 * tracking — `trackPageView` is a no-op there, and the cookie banner
 * doesn't surface either, so consent stays denied for those visits.
 */

const GA_MEASUREMENT_ID = 'G-5KPBD1LWQW';
const CONSENT_KEY = 'ga_consent';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export type ConsentState = 'granted' | 'denied' | null;

/**
 * Returns true if the given pathname is a marketing/blog page where we want
 * to track traffic. App routes are excluded so logged-in user activity
 * doesn't pollute marketing stats.
 */
export function isTrackedRoute(pathname: string): boolean {
  const excluded = [
    '/dashboard',
    '/guest',
    '/admin',
    '/sign-in',
    '/sign-up',
    '/embed',
  ];
  if (excluded.some((p) => pathname === p || pathname.startsWith(p + '/'))) {
    return false;
  }

  if (pathname === '/') return true;

  const trackedSuffixes = [
    '/blog',
    '/features',
    '/pricing',
    '/about',
    '/contact',
    '/za-iznajmljivace',
    '/za-agencije',
    '/za-vile',
    '/privacy',
    '/terms',
    '/cookies',
  ];
  return trackedSuffixes.some(
    (s) => pathname === s || pathname.startsWith(s + '/') ||
           /^\/[a-z]{2}(\/|$)/.test(pathname) && (pathname.endsWith(s) || pathname.includes(s + '/'))
  );
}

export function getConsent(): ConsentState {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
}

export function setConsent(value: 'granted' | 'denied'): void {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // private browsing / quota — fine to ignore
  }
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('consent', 'update', {
    analytics_storage: value === 'granted' ? 'granted' : 'denied',
  });
}

/**
 * On app startup, re-apply any saved consent so the user doesn't have to
 * re-accept on every visit. Called once from AnalyticsProvider.
 */
export function applyStoredConsent(): void {
  const v = getConsent();
  if (v) setConsent(v);
}

/**
 * Manual page_view for SPA route changes. gtag's auto-page-view only fires
 * on full document loads, so we mirror it on client-side navigation.
 * No-ops on app routes and when consent is denied.
 */
export function trackPageView(path: string, title?: string): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  if (getConsent() !== 'granted') return;
  if (!isTrackedRoute(path)) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.href,
    send_to: GA_MEASUREMENT_ID,
  });
}

export function trackEvent(
  name: string,
  params?: Record<string, unknown>
): void {
  if (typeof window === 'undefined' || !window.gtag) return;
  if (getConsent() !== 'granted') return;
  window.gtag('event', name, { ...(params ?? {}), send_to: GA_MEASUREMENT_ID });
}

// Kept for backwards compatibility — anything still importing initAnalytics
// gets a no-op since the tag is now loaded from index.html.
export function initAnalytics(): void {
  applyStoredConsent();
}
